/**
 * globalDocDesigns - persistence for Global Document designs
 * OUTSIDE the editor component: the gallery's Upload flow and its
 * My Templates list.
 *
 * Channels, mirroring what useHistorySnapshots does inside the
 * editor:
 * - Account: POST/GET/DELETE /api/designs with kind 'globaldoc'
 *   (owner-scoped, JWT). A dead token (401/403) NEVER loses the
 *   design: the save falls back to this browser and says so.
 * - This browser: uploads are kept in a list under
 *   `oc-globaldoc-uploads`; the editor's own explicit Save lives
 *   under `oc-globaldoc-design` (owned by the history hook).
 *
 * Opening a design writes the same envelopes the editor's account
 * loader writes (explicit-Save copy + working state), so the
 * editor restores it verbatim on mount.
 */
import { serializeWorkState } from '../hooks/useHistorySnapshots';
import { isAuthFailureStatus, notifyAuthExpired } from './authExpiry';

export const GLOBALDOC_KIND = 'globaldoc';
export const GLOBALDOC_STORAGE_KEY = 'oc-globaldoc-design';
export const GLOBALDOC_UPLOADS_KEY = 'oc-globaldoc-uploads';
/** Account payload cap is 1.5 MB; keep a safety margin for the envelope. */
export const GLOBALDOC_MAX_SAVE_BYTES = 1_400_000;

export interface LocalUpload {
    id: string;
    name: string;
    at: number;
    state: any;
}

export interface SavedDesignRow {
    id: string;
    name: string;
    at: number;
    source: 'account' | 'upload' | 'editor-save';
    state?: any; // present for local rows; account rows are fetched on open
}

const authToken = (): string | null => {
    try { return localStorage.getItem('token'); } catch { return null; }
};

export function readLocalUploads(): LocalUpload[] {
    try {
        const raw = localStorage.getItem(GLOBALDOC_UPLOADS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(u => u && typeof u.id === 'string' && u.state);
    } catch { return []; }
}

export function writeLocalUploads(list: LocalUpload[]): boolean {
    try {
        localStorage.setItem(GLOBALDOC_UPLOADS_KEY, JSON.stringify(list));
        return true;
    } catch { return false; }
}

export function addLocalUpload(name: string, state: any): LocalUpload | null {
    const entry: LocalUpload = {
        id: `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name,
        at: Date.now(),
        state
    };
    const list = [entry, ...readLocalUploads()];
    return writeLocalUploads(list) ? entry : null;
}

export function removeLocalUpload(id: string): void {
    writeLocalUploads(readLocalUploads().filter(u => u.id !== id));
}

/** The editor's own explicit-Save copy in this browser, if any. */
export function readEditorSave(): { at: number; name: string; state: any } | null {
    try {
        const raw = localStorage.getItem(GLOBALDOC_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.state) {
            return { at: parsed.at || 0, name: (typeof parsed.name === 'string' && parsed.name.trim()) ? parsed.name : 'Saved design', state: parsed.state };
        }
    } catch { /* corrupt: treated as absent */ }
    return null;
}

export interface AccountDesignRow { id: string; name: string; updated_at: string }

export async function fetchAccountDesigns(): Promise<{ designs: AccountDesignRow[]; authFailed: boolean }> {
    const token = authToken();
    if (!token) return { designs: [], authFailed: false };
    try {
        const res = await fetch('/api/designs', { headers: { 'Authorization': `Bearer ${token}` } });
        if (isAuthFailureStatus(res.status)) {
            notifyAuthExpired();
            return { designs: [], authFailed: true };
        }
        const json = await res.json().catch(() => null);
        if (json?.success && Array.isArray(json.designs)) {
            return {
                designs: json.designs
                    .filter((d: any) => d.kind === GLOBALDOC_KIND)
                    .map((d: any) => ({ id: d.id, name: d.name, updated_at: d.updated_at })),
                authFailed: false
            };
        }
    } catch { /* offline: local rows still list */ }
    return { designs: [], authFailed: false };
}

export async function fetchAccountDesignData(id: string): Promise<any | null> {
    const token = authToken();
    if (!token) return null;
    try {
        const res = await fetch(`/api/designs/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (isAuthFailureStatus(res.status)) { notifyAuthExpired(); return null; }
        const json = await res.json().catch(() => null);
        return (res.ok && json?.success && json.design?.data) ? json.design.data : null;
    } catch { return null; }
}

export async function deleteAccountDesign(id: string): Promise<boolean> {
    const token = authToken();
    if (!token) return false;
    try {
        const res = await fetch(`/api/designs/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (isAuthFailureStatus(res.status)) { notifyAuthExpired(); return false; }
        return res.ok;
    } catch { return false; }
}

export type SaveDesignOutcome =
    | { where: 'account' }
    | { where: 'local'; reason: 'signed-out' | 'auth-failed' | 'too-large' | 'server' | 'quota' };

/**
 * Save a document design snapshot: to the account when signed in,
 * otherwise (or on any account failure) to this browser's upload
 * list. The design is never dropped silently — the outcome says
 * exactly where it landed and why.
 */
export async function saveGlobalDocDesign(name: string, state: any): Promise<SaveDesignOutcome> {
    const token = authToken();
    if (token) {
        const body = JSON.stringify({ name, kind: GLOBALDOC_KIND, data: state });
        if (body.length <= GLOBALDOC_MAX_SAVE_BYTES) {
            try {
                const res = await fetch('/api/designs', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body
                });
                if (res.ok) {
                    const json = await res.json().catch(() => null);
                    if (json?.success) return { where: 'account' };
                }
                if (isAuthFailureStatus(res.status)) {
                    notifyAuthExpired();
                    return addLocalUpload(name, state)
                        ? { where: 'local', reason: 'auth-failed' }
                        : { where: 'local', reason: 'quota' };
                }
                return addLocalUpload(name, state)
                    ? { where: 'local', reason: 'server' }
                    : { where: 'local', reason: 'quota' };
            } catch {
                return addLocalUpload(name, state)
                    ? { where: 'local', reason: 'server' }
                    : { where: 'local', reason: 'quota' };
            }
        }
        // Too large for the account channel: still keep it locally if it fits.
        return addLocalUpload(name, state)
            ? { where: 'local', reason: 'too-large' }
            : { where: 'local', reason: 'quota' };
    }
    return addLocalUpload(name, state)
        ? { where: 'local', reason: 'signed-out' }
        : { where: 'local', reason: 'quota' };
}

/**
 * Stage a design so the editor opens it: write the explicit-Save
 * envelope AND the working-state envelope under the editor's keys
 * (the same pair its account loader writes), then navigate.
 */
export function openDesignInEditor(state: any, name: string): void {
    try {
        localStorage.setItem(GLOBALDOC_STORAGE_KEY, JSON.stringify({ at: Date.now(), name, state }));
    } catch { /* the working-state write below is the restore path that matters */ }
    try {
        localStorage.setItem(`${GLOBALDOC_STORAGE_KEY}:work`, serializeWorkState(state));
    } catch { /* storage unavailable: the editor will open on its default template */ }
    window.location.assign('/document-editor');
}
